import { apiClient } from '@/lib/api-client'
import { config } from '@/config'
import { textFragment } from '@/lib/utils'

export interface DocumentItem {
  id: number
  title: string
  short_name?: string | null
  document_type?: string | null
  author?: string | null
  publication_year?: number | null
  publisher?: string | null
  licence?: string | null
  is_valid: boolean
  current_state?: string | null
  chunk_count: number
  term_count: number
  collection_id?: number | null
  date_created?: string | null
  created_by_name?: string | null
  /** NULL = extraction never ran (different from 'ran, found nothing'). */
  terms_extracted_at?: string | null
  /** Comma-separated codes ("et", "en,et") — a document can be multilingual. */
  languages?: string | null
  /** Comma-separated, inherited from the terms found in this document. */
  domains?: string | null
  /** The original PDF is stored; see documentFileUrl(). */
  has_file?: boolean
}

/**
 * The original PDF, served inline by the backend. A plain link on purpose:
 * the session cookie travels with it and the browser does the rendering.
 *
 * Every viewer reads a different part of the fragment, so it carries both:
 *  - `page=N` — Adobe open parameter, honoured by Chrome, Firefox, Safari, Acrobat.
 *  - `search=word` — Adobe open parameter; Firefox (pdf.js) and Acrobat run a
 *    find and highlight the hits. Chrome's viewer ignores the key.
 *  - `:~:text=word` — the text directive; Chrome's PDF viewer applies it on
 *    load since early 2025 (open_pdf_params_parser.getTextFragments), Firefox
 *    and Safari ignore it in PDFs. It goes last because Chrome splits the
 *    hash on `:~:` and parses only what precedes it as open parameters.
 */
export function documentFileUrl(id: number, page?: number | null, surface?: string | null) {
  const base = `${config.BACKEND_URL}/documents/${id}/file`
  const params: string[] = []
  if (page && page > 0) params.push(`page=${page}`)
  const word = surface?.trim()
  if (word) params.push(`search=${encodeURIComponent(word)}`, textFragment(word))
  return params.length ? `${base}#${params.join('&')}` : base
}

export async function getDocuments(collectionId: number | null): Promise<DocumentItem[]> {
  const { data } = await apiClient.get<{ documents: DocumentItem[] }>('/documents', {
    params: collectionId ? { collection_id: collectionId } : {},
  })
  return data.documents ?? []
}

/**
 * documents.document_type values, in the order the UI offers them. Mirrors
 * DOCUMENT_TYPES on the server; the labels live in the catalogue under
 * documents.types.*. 'guidline' is the enum's own spelling.
 */
export const DOCUMENT_TYPES = [
  'legal_act',
  'standard',
  'educational',
  'thesis',
  'ba_thesis',
  'ma_thesis',
  'phd_thesis',
  'article',
  'glossary',
  'guidline',
  'report',
  'media',
  'social_media',
  'other',
] as const

export type DocumentType = (typeof DOCUMENT_TYPES)[number]

export function isDocumentType(v: string | null | undefined): v is DocumentType {
  return (DOCUMENT_TYPES as readonly string[]).includes(v ?? '')
}

/**
 * Everything the EKI source table (source_metadata.xlsx) records about a
 * document: file name, URL, date accessed, type, year, title, author,
 * publisher, languages, translation, licence, validity and comments.
 */
export interface DocumentDetail {
  id: number
  title: string
  /** Name of the uploaded file — read-only. */
  filename?: string | null
  short_name?: string | null
  document_type?: string | null
  author?: string | null
  publication?: string | null
  publication_year?: number | null
  publisher?: string | null
  licence?: string | null
  url?: string | null
  /** YYYY-MM-DD */
  date_accessed?: string | null
  languages?: string | null
  is_translation: boolean
  keywords: string[]
  is_valid: boolean
  /** YYYY-MM-DD; null = no expiry. */
  valid_until?: string | null
  comments?: string | null
  current_state?: string | null
  date_created?: string | null
  date_modified?: string | null
  collection_id?: number | null
  uuid?: string | null
  /** The original PDF is stored; see documentFileUrl(). */
  has_file?: boolean
  chunk_count: number
}

export async function getDocument(id: number): Promise<DocumentDetail> {
  const { data } = await apiClient.get<DocumentDetail>(`/documents/${id}`)
  return data
}

/**
 * Fields the PUT accepts. Only keys present are touched: a key sent as ""
 * or null clears that field, a key left out keeps its value.
 */
export interface DocumentPatch {
  title?: string
  short_name?: string
  document_type?: DocumentType
  author?: string
  publication?: string
  publication_year?: number | null
  publisher?: string
  licence?: string
  url?: string
  date_accessed?: string
  languages?: string
  is_translation?: boolean
  is_valid?: boolean
  valid_until?: string
  comments?: string
}

export async function updateDocument(id: number, patch: DocumentPatch) {
  const { data } = await apiClient.put(`/documents/${id}`, patch)
  return data
}

/** Removes the document, its Qdrant chunks and every mention it contributed. */
export async function deleteDocument(id: number) {
  const { data } = await apiClient.delete(`/documents/${id}`)
  return data
}

export interface DocumentChunk {
  text: string
  page_number: number
}

export async function getDocumentText(id: number): Promise<DocumentChunk[]> {
  const { data } = await apiClient.get<{ chunks?: DocumentChunk[] }>(`/documents/${id}/text`)
  return data.chunks ?? []
}

export interface DocumentContext {
  page: number
  /** The passage; empty when the quote could not be located in the stored text. */
  text: string
  /** Offsets of the quote inside `text`. */
  start: number
  end: number
  truncated_start: boolean
  truncated_end: boolean
}

/** The paragraph around a quoted sentence, with the quote's offsets in it. */
export async function getDocumentContext(
  id: number,
  q: string,
  page = 0,
): Promise<DocumentContext> {
  const { data } = await apiClient.get<DocumentContext>(`/documents/${id}/context`, {
    params: { q, page },
  })
  return data
}

export interface DocumentTerm {
  term_id: number
  lemma: string
  lang: string
  domain: string
  term_type: string
  review_status: string
  mentions: { surface: string; sentence: string; page: number }[]
}

/** Terms occurring in this document — used to highlight its text. */
export async function getDocumentTerms(id: number): Promise<DocumentTerm[]> {
  const { data } = await apiClient.get<{ terms: DocumentTerm[] }>(`/documents/${id}/terms`)
  return data.terms ?? []
}
